import PropTypes from 'prop-types';
import { useMemo, useRef } from 'react';

import SidebarAdSlot from 'components/AdSlots/SidebarAdSlot';
import CategoryNav from 'components/Category/Nav';
import Sidebar from 'components/Sidebar';
import StoresWithFeaturedCoupons from 'components/Sidebar/HighlightedStores/V1';

import SubcategorySidebarBottomSection from './SidebarBottomSection';

const SubcategorySidebar = ({
  category,
  featuredStores,
  gaEventCategory,
  isGoogleAdActive = false,
  offersFilters,
  position,
  stores,
  subcategory,
  onNavItemClick,
  onOffersFiltersReset,
  onOffersPriceRangeFilterChange,
  onOffersStoresFilterChange,
  onOffersVenueFilterChange,
}) => {
  const navRef = useRef(null);
  const filtersRef = useRef(null);
  const sidebarGoogleAdRef = useRef(null);
  const storesWithFeaturedCouponsRef = useRef(null);

  const refsToBeIgnoredBeforeFixing = useMemo(() => {
    const refs = [navRef, filtersRef];

    if (isGoogleAdActive) {
      refs.push(storesWithFeaturedCouponsRef, sidebarGoogleAdRef);
    }

    return refs;
  }, [isGoogleAdActive]);

  return (
    <Sidebar
      className="mb-8 gap-y-4"
      position={position}
      refsToBeIgnoredBeforeFixing={refsToBeIgnoredBeforeFixing}
    >
      <CategoryNav
        ref={navRef}
        activeSubcategory={subcategory}
        categoryName={category.categoryName}
        categorySlug={category.categorySlug}
        className="hidden lg:flex"
        subcategories={category.subcategories}
        onNavItemClick={onNavItemClick}
      />
      <SubcategorySidebarBottomSection
        forwardedFiltersRef={filtersRef}
        gaEventCategory={gaEventCategory}
        offersFilters={offersFilters}
        stores={stores}
        onOffersFiltersReset={onOffersFiltersReset}
        onOffersPriceRangeFilterChange={onOffersPriceRangeFilterChange}
        onOffersStoresFilterChange={onOffersStoresFilterChange}
        onOffersVenueFilterChange={onOffersVenueFilterChange}
      />
      {isGoogleAdActive && <SidebarAdSlot ref={sidebarGoogleAdRef} />}
      <StoresWithFeaturedCoupons
        ref={storesWithFeaturedCouponsRef}
        stores={featuredStores}
      />
      {isGoogleAdActive && <SidebarAdSlot />}
    </Sidebar>
  );
};

SubcategorySidebar.propTypes = {
  category: PropTypes.shape({
    categoryId: PropTypes.number.isRequired,
    categoryName: PropTypes.string.isRequired,
    categorySlug: PropTypes.string.isRequired,
    subcategories: PropTypes.arrayOf(
      PropTypes.shape({
        isSelected: PropTypes.bool.isRequired,
        subcategoryId: PropTypes.number.isRequired,
        subcategoryName: PropTypes.string.isRequired,
        subcategorySlug: PropTypes.string.isRequired,
        deepSubcategories: PropTypes.arrayOf(
          PropTypes.shape({
            deepSubcategoryId: PropTypes.number.isRequired,
            deepSubcategoryName: PropTypes.string.isRequired,
            deepSubcategorySlug: PropTypes.string.isRequired,
            isSelected: PropTypes.bool.isRequired,
          })
        ),
      })
    ),
  }).isRequired,
  featuredStores: PropTypes.arrayOf(
    PropTypes.shape({
      storeSlug: PropTypes.string,
      storeName: PropTypes.string,
    })
  ).isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  isGoogleAdActive: PropTypes.bool,
  offersFilters: PropTypes.shape({
    priceRange: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
    }).isRequired,
    stores: PropTypes.arrayOf(
      PropTypes.shape({
        storeId: PropTypes.number.isRequired,
        storeName: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      storeId: PropTypes.number.isRequired,
      storeName: PropTypes.string.isRequired,
    })
  ).isRequired,
  subcategory: PropTypes.shape({
    articles: PropTypes.arrayOf(
      PropTypes.shape({
        articleId: PropTypes.number.isRequired,
        articleDescription: PropTypes.string.isRequired,
        articleSlug: PropTypes.string.isRequired,
        articleTitle: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  onNavItemClick: PropTypes.func.isRequired,
  onOffersFiltersReset: PropTypes.func.isRequired,
  onOffersPriceRangeFilterChange: PropTypes.func.isRequired,
  onOffersStoresFilterChange: PropTypes.func.isRequired,
  onOffersVenueFilterChange: PropTypes.func.isRequired,
};

export default SubcategorySidebar;
